import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle, TrendingUp } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

export default function DistrictPage({ config }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.h1,
    provider: {
      "@type": "RealEstateAgent",
      name: "BRIMO Immobilien NRW",
      telephone: "+4921293766829",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Wilhelmstraße 3",
        addressLocality: "Haan",
        postalCode: "42781",
        addressCountry: "DE",
      },
    },
    areaServed: config.name,
    description: config.description,
  };

  useMeta(config.title, config.description);
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [config.name]);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end pb-16" data-testid="district-hero">
        <div className="absolute inset-0">
          <img
            src={config.heroImage}
            alt={`Immobilie verkaufen ${config.name}`}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <nav className="flex items-center gap-2 text-gray-500 text-xs mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#E61D38] transition-colors">Start</Link>
            <span>/</span>
            <Link to="/immobilie-verkaufen-kreis-mettmann" className="hover:text-[#E61D38] transition-colors">
              Verkaufen
            </Link>
            <span>/</span>
            <span className="text-gray-400">{config.name}</span>
          </nav>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {config.h1.toUpperCase()}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed mb-8">{config.about}</p>
          <Link
            to="/marktwertermittlung-kreis-mettmann"
            className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors w-fit"
            data-testid="district-cta"
          >
            Kostenlose Bewertung in {config.name}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="district-content">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Market Info */}
              <div className="mb-12">
                <div className="h-0.5 w-8 bg-[#E61D38] mb-4" />
                <h2
                  className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-5"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  DER IMMOBILIENMARKT IN {config.name.toUpperCase()}
                </h2>
                <p className="text-gray-400 leading-relaxed text-sm mb-6">{config.marketInfo}</p>

                {/* Price Range */}
                <div className="bg-[#0d0d0d] border border-white/5 p-6 flex items-center gap-5">
                  <TrendingUp size={24} className="text-[#E61D38] flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">
                      Durchschnittliche Quadratmeterpreise
                    </p>
                    <p className="text-white font-bold text-lg">{config.priceRange}</p>
                    <p className="text-gray-600 text-xs mt-1">Abhängig von Lage, Zustand und Objekttyp</p>
                  </div>
                </div>
              </div>

              {/* Characteristics */}
              <div className="mb-12">
                <div className="h-0.5 w-8 bg-[#E61D38] mb-4" />
                <h2
                  className="text-2xl font-black text-white uppercase tracking-tight mb-6"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  MERKMALE VON {config.name.toUpperCase()}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {config.characteristics?.map((char, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-[#262626] border border-white/5 p-4"
                      data-testid={`district-char-${i}`}
                    >
                      <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{char}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Why Sell with BRIMO */}
              <div className="bg-[#262626] border border-white/5 p-8">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-6"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Immobilie verkaufen in {config.name} – warum BRIMO?
                </h3>
                <ul className="space-y-3">
                  {[
                    `Tiefe Marktkenntnis in ${config.name} und im gesamten Kreis Mettmann`,
                    "Umfassendes lokales Käufernetzwerk",
                    "Professionelle Vermarktung auf allen Portalen",
                    "Strukturierter Verkaufsprozess ohne Stress",
                    "Persönliche Begleitung bis zum Notartermin",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Other Districts */}
              <div className="mt-10">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-5"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Weitere Städte
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {cityConfig.districts
                    .filter((d) => d.slug !== config.slug)
                    .map((d) => (
                      <Link
                        key={d.slug}
                        to={`/immobilie-verkaufen-${d.slug}`}
                        className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#E61D38] transition-colors bg-[#262626] px-4 py-3 border border-white/5 hover:border-[#E61D38]/30"
                        data-testid={`other-district-${d.slug}`}
                      >
                        <MapPin size={10} className="text-[#E61D38]" />
                        {d.name}
                      </Link>
                    ))}
                </div>
              </div>
            </div>

            {/* Sidebar Form */}
            <div>
              <div className="sticky top-24">
                <LeadForm
                  formType="seller"
                  title={`Immobilie in ${config.name} bewerten`}
                  ctaText="Marktwert anfragen"
                  showDistrict={true}
                  showPropertyType={true}
                  pageSource={`/immobilie-verkaufen-${config.slug}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
